import { useEffect } from 'react';

import { useForm, Controller, FieldValues } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'styled-components';
import dynamic from 'next/dynamic';
import Link from 'next/link';

import Typography from '@components/Typography';
import { Field } from '@components/utils/form';
import PersonName from '@components/ui/form/PersonName';
import { TextAreaBox } from '@components/ui/form/common/styled';
import { Col, FlexibleDiv } from '@components/formStyles';
import { RadioGroup } from '@components/RadioGroup';
import { PhoneNumber } from '@components/ui/form/FormattedInputs';
import { ErrorViewModel } from '@api/index';
import { DateType, formatMomentDate } from '@utils/dateUtil';
import { AlertType } from '@components/views/hotel/detail/components/room-selection/components/Alert';
import { useDevice } from '@contexts/device';
import { ContactType } from '@api/v3';
import { logFormErrors } from '@utils/gtm/error-log-gtm';
import CustomIcon from '@components/utils/CustomIcon';
import { WarningCircleIcon } from '@assets/icons';

import { LetUsCallYouFormData } from './types';
import {
  InfoText,
  InfoBoldText,
  RequiredWarningText,
  NameCol,
  Wrapper,
  StyledRow,
  ApplyButton,
  schema,
  AvailableTimeData,
  KvkkInfoUnderlined,
  StyledAvailableTime,
  ApplyButtonWrapper,
  Container,
  InfoWarning,
} from './LetUsCallYouModalSchemeAndStyle';

const Modal = dynamic(() => import('@components/Modal/next/Modal'), {
  ssr: false,
});

const LetUsCallYouModal = ({
  onSubmit,
  onCancel,
  entityName,
  formCheckInDate,
  formCheckOutDate,
  isPostCallMeLoading,
  alertType = AlertType.PRIMARY,
  hideCloseIcon,
  preventBodyScroll = true,
  errorMessage,
}: {
  onSubmit: (formData: LetUsCallYouFormData) => void;
  onCancel?: () => void;
  entityName: string;
  formCheckInDate: DateType | null;
  formCheckOutDate: DateType | null;
  isPostCallMeLoading: boolean;
  alertType?: AlertType;
  isPostCallMeError?: ErrorViewModel[];
  hideCloseIcon?: boolean;
  preventBodyScroll?: boolean;
  errorMessage?: string;
}) => {
  const { isDesktop } = useDevice();

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors },
  } = useForm<LetUsCallYouFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      availableTime: AvailableTimeData?.[0]?.value,
      contactOption: ContactType.PHONE,
      message: '',
    },
    resolver: yupResolver(schema),
  });
  const selectedContactOption = watch('contactOption');
  useEffect(() => {
    const body = document.querySelector('body');

    if (body) {
      if (preventBodyScroll) {
        body.style.overflow = 'hidden';
      } else {
        body.style.overflow = 'initial';
      }
    }

    return () => {
      if (body) {
        body.style.overflow = 'initial';
      }
    };
  }, [preventBodyScroll]);
  const onError = (errors: FieldValues) => {
    if (errors) {
      const fieldNames = Object.keys(errors).join(', ');
      logFormErrors('LetUsCallYouForm', fieldNames);
    }
  };
  return (
    <Modal
      width={560}
      borderRadius={8}
      title={
        <Typography variant='Title4' color='D400' weight='SemiBold'>
          {alertType === AlertType.PRIMARY
            ? 'Biz Sizi Arayalım'
            : 'Kontenjan Talep Et'}
        </Typography>
      }
      onCancel={onCancel}
      padding={isDesktop ? '20px' : '16px 0 0 0'}
      headerElementCss={
        isDesktop
          ? undefined
          : css`
              padding: 0 16px;
            `
      }
      hideCloseIcon={hideCloseIcon}
    >
      <>
        {errorMessage ? (
          <InfoWarning>
            <CustomIcon icon={WarningCircleIcon} fontSize={24} />
            {errorMessage}
          </InfoWarning>
        ) : null}

        <Container>
          <InfoText>
            <InfoBoldText>
              {entityName} {formatMomentDate(formCheckInDate)}
              {formCheckOutDate
                ? ` - ${formatMomentDate(formCheckOutDate)}`
                : ''}
            </InfoBoldText>
            &nbsp;
            {alertType === AlertType.PRIMARY
              ? 'müsaitliği hakkında bilgi almak için aşağıdaki formu doldurabilirsiniz.'
              : 'kontenjan talep etmek için aşağıdaki formu doldurabilirsiniz.'}
          </InfoText>

          <RequiredWarningText>
            (*) Doldurulması zorunlu alanlar.
          </RequiredWarningText>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Wrapper>
              <NameCol>
                <Controller
                  name='firstName'
                  key='firstName'
                  control={control}
                  render={({ field }) => (
                    <Field
                      Component={PersonName}
                      errorMessage={errors.firstName?.message}
                      errorPropName='error'
                      hasError={!!errors.firstName?.message}
                      label='Ad*'
                      {...field}
                    />
                  )}
                />
                <Controller
                  key='lastName'
                  name='lastName'
                  control={control}
                  render={({ field }) => (
                    <Field
                      Component={PersonName}
                      errorMessage={errors.lastName?.message}
                      errorPropName='error'
                      hasError={!!errors.lastName?.message}
                      label='Soyad*'
                      {...field}
                    />
                  )}
                />
              </NameCol>

              {selectedContactOption === ContactType.PHONE ? (
                <Controller
                  key='phone'
                  name='phone'
                  control={control}
                  render={({ field }) => (
                    <Field
                      Component={PhoneNumber}
                      errorMessage={errors.phone?.message}
                      errorPropName='error'
                      hasError={!!errors.phone?.message}
                      label='Telefon*'
                      {...field}
                    />
                  )}
                />
              ) : null}
              <Controller
                key='message'
                name='message'
                control={control}
                render={({ field }) => (
                  <Field
                    Component={TextAreaBox}
                    placeholder='Mesajınız (Opsiyonel)'
                    rows={5}
                    {...field}
                  />
                )}
              />

              <StyledRow>
                <Col>
                  <Typography
                    variant={isDesktop ? 'caption' : 'Title5'}
                    weight='SemiBold'
                    color='D400'
                  >
                    Size Ne Zaman Ulaşalım?
                  </Typography>
                  <FlexibleDiv top={10} />

                  <Controller
                    key='availableTime'
                    name='availableTime'
                    control={control}
                    render={({ field }) => (
                      <StyledAvailableTime>
                        <Field
                          Component={(props) => (
                            <RadioGroup
                              options={AvailableTimeData}
                              getValue={(option) => option.value}
                              getLabel={(option) => option.label}
                              direction={isDesktop ? 'row' : 'column'}
                              selectedIndex={AvailableTimeData.findIndex(
                                (d) => d.value === getValues('availableTime')
                              )}
                              {...props}
                            />
                          )}
                          {...field}
                        />
                      </StyledAvailableTime>
                    )}
                  />
                </Col>
              </StyledRow>

              {/* // we will hide the kvkk related parts for now, we will show them later
            <LetUsCallYouModalKvkkComponent
            selectedContactOption={selectedContactOption}
            handleKvkkChange={handleKvkkChange}
            controlCheckForKvkk={controlCheck}
          /> */}
              <div>
                Kişisel verilerinizin işlenmesi ile ilgili detaylara{' '}
                <KvkkInfoUnderlined>
                  <Link
                    style={{ color: 'black' }}
                    target='_blank'
                    rel='noreferrer noopener'
                    href='https://www.setur.com.tr/kisisel-verilerin-korunmasi'
                    prefetch={false}
                  >
                    {' '}
                    Setur Müşteri Kişisel Verilerin İşlenmesi Aydınlatma Metni
                  </Link>
                </KvkkInfoUnderlined>
                ’nden ulaşabilirsiniz.
              </div>

              {isDesktop ? (
                <StyledRow
                  justifyContent='center'
                  style={{
                    margin: 'unset',
                    width: '100%',
                  }}
                >
                  <ApplyButtonWrapper>
                    <ApplyButton
                      block
                      loading={isPostCallMeLoading}
                      disabled={isPostCallMeLoading}
                      type='submit'
                      variant='Primary'
                    >
                      Gönder
                    </ApplyButton>
                  </ApplyButtonWrapper>
                </StyledRow>
              ) : null}
            </Wrapper>
          </form>
        </Container>
        {isDesktop ? null : (
          <StyledRow
            justifyContent='center'
            style={{ margin: 'auto', width: '100%', marginTop: '20px' }}
          >
            <ApplyButtonWrapper>
              <ApplyButton
                block
                loading={isPostCallMeLoading}
                disabled={isPostCallMeLoading}
                type='submit'
                variant='Primary'
                onClick={handleSubmit(onSubmit)}
              >
                Gönder
              </ApplyButton>
            </ApplyButtonWrapper>
          </StyledRow>
        )}
      </>
    </Modal>
  );
};

export default LetUsCallYouModal;
